import {
    InstantRefundPolicy,
    getInstantRefundPolicy,
} from '@/lib/api/financial/financial-settings-api'
import { useEffect, useState } from 'react'

/**
 * Cache de sessão da política.
 *
 * Mesma razão do `use-offline-settlement-policy`: o modal de estorno aparece na
 * cobrança e no `BillingInfoSection` de exame, consulta, procedimento e
 * teleconsulta — sem isto, cada tela repetiria a requisição de uma configuração
 * administrativa que muda raramente.
 */
let cached: InstantRefundPolicy | null = null
let inFlight: Promise<InstantRefundPolicy | null> | null = null

function fetchPolicy(): Promise<InstantRefundPolicy | null> {
    if (cached) return Promise.resolve(cached)
    if (inFlight) return inFlight

    inFlight = getInstantRefundPolicy()
        .then(res => {
            // Sem permissão ou erro: assume DESLIGADA — o fluxo com fila, que é o
            // padrão. Errar para o lado restritivo aqui só mostra o modal de
            // solicitação; o contrário ofereceria um estorno imediato que o backend
            // recusaria depois de o operador digitar a senha.
            if (!res.success || !res.result) return null
            cached = res.result
            return cached
        })
        .catch(() => null)
        .finally(() => {
            inFlight = null
        })

    return inFlight
}

/** Invalida o cache após salvar a política (tela de configurações). */
export function resetInstantRefundPolicyCache(): void {
    cached = null
}

/**
 * O estorno é feito na hora (com senha) ou vira solicitação para o operador
 * financeiro aprovar?
 */
export function useInstantRefundPolicy(): { allowInstantRefund: boolean } {
    const [allowInstantRefund, setAllowInstantRefund] = useState(
        cached?.allowInstantRefund ?? false
    )

    useEffect(() => {
        let cancelled = false
        fetchPolicy().then(policy => {
            if (!cancelled && policy)
                setAllowInstantRefund(policy.allowInstantRefund)
        })
        return () => {
            cancelled = true
        }
    }, [])

    return { allowInstantRefund }
}
